/**
 * Audit events emitted by DateCapture during a date capture session.
 *
 * These model AFTConanService's RecordAudit AuditEvent contract. DateCapture
 * is purely a UI/capture orchestrator: it accumulates these events — both
 * user actions and OCR results derived from the extraction response — and
 * returns them to the host application, which is responsible for the actual
 * RecordAudit call and for deciding the audit outcome.
 */

import { DateExtractionType } from "./DateExtractionType"
import { DateOcrExtractionResult } from "./DateOcrExtractionProvider"

/** Reason an OCR extraction failed. */
export type OcrFailureReason =
  | "BLURRY"
  | "CUTOFF"
  | "DEPENDENCY"
  | "GLARE"
  | "HUMAN_FACE_DETECTED"
  | "INCONCLUSIVE"
  | "LOW_CONTRAST"
  | "OBSTRUCTED"

/** A failure reason an extraction result can actually carry. */
export type ExtractionFailureReason =
  NonNullable<DateOcrExtractionResult["failureReason"]>

/** Whether another frame of the same scene is worth sending. */
export type PollFailureClass = "retryable" | "terminal"

/** What one failure reason means for polling and for the audit trail. */
export interface FailureReasonPolicy {
  poll: PollFailureClass
  audit: OcrFailureReason
}

/**
 * The single place to edit when a failure reason is added or reclassified.
 *
 * `Record` makes a missing row a compile error at this declaration, so the
 * table cannot fall behind the `failureReason` union.
 *
 * `poll` asks whether the reason describes the SCENE — retryable, because a
 * polling session re-samples an evolving scene as the worker moves the product
 * — or the channel, the product or the worker, which reads the same on every
 * frame and is terminal. `audit` collapses the twelve result reasons onto the
 * eight `OcrFailureReason` accepts.
 *
 * Declaration order is the runtime order of ALL_POLL_FAILURE_REASONS below.
 */
export const FAILURE_REASON_POLICY:
  Record<ExtractionFailureReason, FailureReasonPolicy> = {
  BLURRY: { poll: "retryable", audit: "BLURRY" },
  CUTOFF: { poll: "retryable", audit: "CUTOFF" },
  GLARE: { poll: "retryable", audit: "GLARE" },
  LOW_CONTRAST: { poll: "retryable", audit: "LOW_CONTRAST" },
  OBSTRUCTED: { poll: "retryable", audit: "OBSTRUCTED" },
  NO_DATE_VISIBLE: { poll: "retryable", audit: "DEPENDENCY" },
  REQUESTED_TYPE_NOT_FOUND: { poll: "terminal", audit: "INCONCLUSIVE" },
  INCONCLUSIVE: { poll: "retryable", audit: "INCONCLUSIVE" },
  HUMAN_FACE_DETECTED: { poll: "retryable", audit: "HUMAN_FACE_DETECTED" },
  DEPENDENCY: { poll: "terminal", audit: "DEPENDENCY" },
  ERROR: { poll: "terminal", audit: "DEPENDENCY" },
  USER_REJECTED: { poll: "terminal", audit: "DEPENDENCY" },
}

export const ALL_POLL_FAILURE_REASONS: readonly ExtractionFailureReason[] =
  Object.keys(FAILURE_REASON_POLICY) as ExtractionFailureReason[]

/**
 * Classify a failure as worth another frame or not.
 */
export function classifyPollFailure(
  reason: DateOcrExtractionResult["failureReason"],
): PollFailureClass {
  return reason === undefined
    ? "terminal"
    : FAILURE_REASON_POLICY[reason].poll
}

/**
 * Map a failure reason onto the one recorded on an audit event
 */
export function toAuditFailureReason(
  reason: DateOcrExtractionResult["failureReason"],
): OcrFailureReason {
  return reason === undefined
    ? "DEPENDENCY"
    : FAILURE_REASON_POLICY[reason].audit
}

/** How the user confirmed a date. */
export type ConfirmationMethod = "YES_NO" | "REKEY"

/** Why the user skipped capture. */
export type SkipReason =
  | "NO_HARDWARE"
  | "FAULTY_HARDWARE"
  | "NO_FEATURE"
  | "ASSOCIATE_SKIPPED"

/** The type of audit event. */
export type DateAuditEventType =
  | "IMAGE_CAPTURE"
  | "OCR_DATE_CAPTURE"
  | "NO_DATE_DETECTED"
  | "OCR_FAILURE"
  | "USER_CONFIRMATION"
  | "USER_DATE_ENTRY"
  | "USER_SKIP"

/** An image was captured for OCR. */
export interface ImageCaptureEventContext {
  /** Device used to capture the image (e.g. "WEBCAM", "PHONE"). */
  captureDeviceType?: string
  /** Hardware identifier of the capture device. */
  captureDeviceId?: string
  /**
   * Number of OCR requests the automatic polling session issued before it
   * produced this frame. Absent when the frame came from a single-shot
   * capture gesture rather than a polling session.
   */
  pollAttemptCount?: number

  autoCaptureUnavailable?: boolean
}

/** OCR successfully extracted a date. */
export interface OcrDateCaptureEventContext {
  /** The extracted date, in YYYY-MM-DD format. */
  date: string
  /** Confidence reported by the OCR provider. */
  /** Confidence in the range [0, 1] where 0 is low and 1 is high. */
  confidence?: number
  /** Raw text the OCR read, e.g. "EXP 12/31/26". */
  rawDateText?: string
  /** S3 URI of the image used for extraction, if available. */
  imageUri?: string
  /**
   * Number of OCR requests the automatic polling session issued before it
   * produced this result. Absent outside a polling session.
   */
  pollAttemptCount?: number
}

/** OCR ran but found no date in the image. */
export interface NoDateEventContext {
  /** S3 URI of the image that showed no date, if available. */
  imageUri?: string
  /**
   * Number of OCR requests the automatic polling session issued before it
   * produced this result. Absent outside a polling session.
   */
  pollAttemptCount?: number
}

/** OCR failed to process the image. */
export interface OcrFailureEventContext {
  /** Why the extraction failed. */
  failureReason: OcrFailureReason
  /** Error detail, if available. */
  errorMessage?: string
  /** S3 URI of the image (absent when failureReason is HUMAN_FACE_DETECTED). */
  imageUri?: string
  /**
   * Number of OCR requests the automatic polling session issued before it
   * produced this result. Absent outside a polling session.
   */
  pollAttemptCount?: number
}

/** The user confirmed a presented date. */
export interface UserConfirmationEventContext {
  /** The value the user confirmed, in YYYY-MM-DD format. */
  confirmedValue: string
  /** How the date was confirmed. */
  confirmationMethod: ConfirmationMethod
}

/** The user manually entered a date. */
export interface UserDateEntryEventContext {
  /** The date the user entered, in YYYY-MM-DD format. */
  date: string
  /** Device used for the entry. */
  captureDeviceType?: string
}

/** The user skipped capture. */
export interface UserSkipEventContext {
  /** Why capture was skipped. */
  skipReason: SkipReason
  /**
   * Poll requests the automatic capture session had issued when the worker
   * skipped. Absent outside a polling session.
   *
   * Records how long a worker was willing to wait before taking over, which is
   * the most direct evidence available on whether the configured pacing suits
   * them.
   */
  pollAttemptCount?: number
}

export type DateAuditEventContext =
  | ImageCaptureEventContext
  | OcrDateCaptureEventContext
  | NoDateEventContext
  | OcrFailureEventContext
  | UserConfirmationEventContext
  | UserDateEntryEventContext
  | UserSkipEventContext

/**
 * A single audit event captured during a date capture session.
 */
export interface DateAuditEvent {
  /** When this event occurred (epoch milliseconds). */
  eventTimestamp: number
  /** The type of event. */
  eventType: DateAuditEventType
  /** Details specific to this event type. */
  eventContext?: DateAuditEventContext
}

/** Where the final selected date came from. */
export type CapturedDateSource = "OCR" | "USER_ENTERED"

/**
 * The final outcome of a date capture session.
 *
 * Models AFTConanService's ExpirationDateAuditOutcome. Tells the backend the
 * date the session resolved to, so it does not need to reconstruct it from
 * the audit event list.
 */
export interface DateCaptureOutcome {
  /** The final date the session resolved to, in YYYY-MM-DD format. */
  selectedDate: string
  /** The date that was captured (OCR-extracted or user-entered). */
  capturedDate: string
  /** Where the selected date came from. */
  capturedDateSource: CapturedDateSource
  /** The type of date the session captured. */
  dateType: DateExtractionType
  /** Year component of the selected date. */
  yearComponent?: string
  /** Month component of the selected date. */
  monthComponent?: string
  /** Day component of the selected date. */
  dayComponent?: string
}

/**
 * How a date capture session ended.
 *
 * `SUCCESS` means a date was resolved, whether by OCR or by hand — an OCR
 * failure the worker recovered from by keying the date in is still a success.
 * Every other value describes a condition that stopped the session before any
 * date existed.
 */
export type DateCaptureStatus =
  | "SUCCESS"
  | "CAMERA_UNAVAILABLE"

/**
 * The full result submitted by DateCapture: how the session ended, the ordered
 * list of audit events, and the final outcome when there is one.
 */
export interface DateCaptureResult {
  /** How the session ended. Read this before touching `outcome`. */
  status: DateCaptureStatus
  /**
   * Ordered list of audit events captured during the session. May be empty
   * when the session ended before anything happened.
   */
  events: DateAuditEvent[]
  /**
   * The final outcome of the session. Present only when `status` is `SUCCESS`;
   * no date was resolved for any other status.
   */
  outcome?: DateCaptureOutcome
}
